import { useCallback, useMemo, useRef, useState } from 'react';

const PLAYBACK_SPEEDS = [1, 1.5, 2] as const;
export type PlaybackSpeed = typeof PLAYBACK_SPEEDS[number];

const nextPlaybackSpeed = (current: PlaybackSpeed): PlaybackSpeed => {
  const nextIndex = (PLAYBACK_SPEEDS.indexOf(current) + 1) % PLAYBACK_SPEEDS.length;
  return PLAYBACK_SPEEDS[nextIndex];
};

type TimeState = { current: number; duration: number };

export type UseAudioPlayerReturnType = {
  audioRef: React.RefObject<HTMLAudioElement>;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  progress: number;
  speed: PlaybackSpeed;
  togglePlay: () => void;
  skip: (seconds: number) => void;
  seekToPercent: (percent: number) => void;
  cycleSpeed: () => void;
  audioEventHandlers: {
    onTimeUpdate: () => void;
    onLoadedMetadata: () => void;
    onPlay: () => void;
    onPause: () => void;
    onEnded: () => void;
  };
};

export function useAudioPlayer(): UseAudioPlayerReturnType {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [time, setTime] = useState<TimeState>({ current: 0, duration: 0 });
  const [speed, setSpeed] = useState<PlaybackSpeed>(1);

  const progress = time.duration > 0 ? (time.current / time.duration) * 100 : 0;

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  }, []);

  const skip = useCallback((seconds: number) => {
    const audio = audioRef.current;
    if (audio) audio.currentTime += seconds;
  }, []);

  const seekToPercent = useCallback((percent: number) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration)) return;
    audio.currentTime = (percent / 100) * audio.duration;
  }, []);

  const cycleSpeed = useCallback(() => {
    setSpeed(prev => {
      const next = nextPlaybackSpeed(prev);
      if (audioRef.current) audioRef.current.playbackRate = next;
      return next;
    });
  }, []);

  const audioEventHandlers = useMemo(
    () => ({
      onTimeUpdate: () => {
        const audio = audioRef.current;
        if (!audio) return;
        setTime({ current: audio.currentTime, duration: audio.duration });
      },
      onLoadedMetadata: () => {
        const audio = audioRef.current;
        if (!audio) return;
        setTime(prev => ({ ...prev, duration: audio.duration }));
      },
      onPlay: () => setIsPlaying(true),
      onPause: () => setIsPlaying(false),
      onEnded: () => setIsPlaying(false)
    }),
    []
  );

  return {
    audioRef,
    isPlaying,
    currentTime: time.current,
    duration: time.duration,
    progress,
    speed,
    togglePlay,
    skip,
    seekToPercent,
    cycleSpeed,
    audioEventHandlers
  };
}
